// Catalog.ID Client — bitcash wallet-sync envelope types
// ============================================================================
// The three `registry-request` modes the BITCASH module — and nothing else —
// may send (cid-encrypted-storage §7.3, §4.6). They are the sanctioned
// exception to the blob store's cryptographic namespace boundary: the wallet
// document lives in the vault's own reserved `cid` namespace, so the module
// cannot derive its key, and the only way it gets the plaintext is a
// consent-gated release whose payload is wrapped to a key the module minted for
// that one request (the `export-mail-encrypt` pattern).
//
// A SEPARATE FAMILY from `bitcash-auth-token`, deliberately. That purpose rides
// the `key-grant-request` envelope, is sent BY OTHER MODULES (cpr, efs, mail),
// and answers with a bounded, revocable spending TOKEN. These three ride the
// `registry-request` envelope, are sent by the bitcash MANAGER alone, and move
// the wallet KEYS themselves. Keeping them apart is what stops a token request
// signed by cpr from ever reaching a handler that releases keys.
//
// v1 modes:
//   - `bitcash-wallets-release`: hand the manager the member's whole wallet
//     document, wrapped to its ephemeral hybrid KEM key. Ceremony-grade AND
//     consent-gated — after this, that machine can spend from every wallet the
//     account holds, so the dialog says exactly that and the decision may be
//     remembered per machine through the ordinary grant policy. REVOKED wallets
//     are included: the manager must know to retire them, which it cannot learn
//     from an absence. The canonical bytes commit to the wrap key's digest —
//     substituting the wrap key IS the attack.
//   - `bitcash-wallets-save-prepare`: leg 1 of the reverse direction. Answers
//     with a CID-side ephemeral hybrid public key, stashed for five minutes
//     (`bitcash-wallets-save-store.ts`). No fields, no consent, no keybag.
//   - `bitcash-wallets-save`: leg 2. ONE wallet the manager minted or imported,
//     wrapped to that key, appended to the member's document at the next free
//     slot. NO consent dialog (§7.3): the module is adding its own wallet to
//     the member's list, the member sees it in Settings, and a dialog per
//     imported wallet would train click-through on the one surface where a
//     member needs to stay awake. The canonical bytes commit to the wrapped
//     blob's digest — substituting it IS the attack here.

import type {
    BitcashWalletsReleaseConsentDecision, BitcashWalletsReleaseConsentInput,
} from './bitcash-wallets-consent-modal';
import type { GrantPolicyEntry } from './grant-policy';
import type { BitcashWalletsDocument, NewWalletRecord } from '../crypto/bitcash-wallets-doc';
import type { KemKeyPair } from '../crypto/kem';

export type BitcashWalletsRequestMode =
    | 'bitcash-wallets-release'
    | 'bitcash-wallets-save-prepare'
    | 'bitcash-wallets-save';

/** The grant-policy key id the release's "allow always" is recorded under.
 *  Per MODE, not per wallet: the release hands over every wallet at once, so
 *  there is no per-wallet decision to remember. */
export const BITCASH_WALLETS_RELEASE_POLICY_KEY = 'bitcash-wallets-release';

export const BITCASH_WALLETS_MODES: readonly BitcashWalletsRequestMode[] = [
    'bitcash-wallets-release', 'bitcash-wallets-save-prepare', 'bitcash-wallets-save',
];

export function isBitcashWalletsMode(mode: string | undefined): mode is BitcashWalletsRequestMode {
    return mode !== undefined
        && (BITCASH_WALLETS_MODES as readonly string[]).includes(mode);
}

export interface BitcashWalletsRequest {
    type: 'registry-request';
    to: string;                  // always "cid" when we receive one
    requestId: string;
    requesterId: string;         // pinned to "bitcash" by the dispatcher
    mode: BitcashWalletsRequestMode;
    requestedAt: string;         // RFC 3339 UTC
    requiresUserInteraction?: boolean;

    // ── bitcash-wallets-release ──
    /** Base64 of the module's EPHEMERAL hybrid KEM wrap public key. MUST decode
     *  to exactly 1600 bytes (X25519 32 ‖ ML-KEM-1024 1568). */
    wrapPubkey?: string;
    /** Base64 SHA-512 of the RAW 1600-byte wrap key — the ONE committed field.
     *  The handler recomputes it from the body before wrapping anything. */
    wrapPubkeyDigestBase64?: string;
    /** Display-only name of the requesting machine, for the consent dialog.
     *  NOT committed; capped and rendered as text, never as markup. */
    deviceLabel?: string;

    // ── bitcash-wallets-save ──
    /** The `requestId` of the `bitcash-wallets-save-prepare` whose ephemeral
     *  key this wallet is wrapped to. Committed. */
    prepareRequestId?: string;
    /** Base64 hybrid-KEM wire blob carrying the wallet JSON. */
    wrappedWallet?: string;
    /** Base64 SHA-512 of the RAW wire blob — the other committed field. */
    wrappedWalletDigestBase64?: string;

    // Module-identity signing fields (verified by the cld-id relay):
    signature?: string;
    signerPubkey?: string;

    // Hub-stamped sender id. Recipients must verify it equals `requesterId`.
    _senderServiceId?: string;
}

export type BitcashWalletsResponseStatus = 'approved' | 'denied' | 'unavailable';

export interface BitcashWalletsResponse {
    type: 'registry-response';
    to: string;
    requestId: string;
    status: BitcashWalletsResponseStatus;
    reason?: string;

    // ── release, approved ──
    /** The generation the released document was stored at. It is part of the
     *  wrap's AAD, so the manager must present it to open the blob — and it is
     *  how the manager tells a replayed older release from the current one. */
    generation?: number;
    /** Base64 hybrid-KEM wire blob: the RFC 8785 canonical JSON of
     *  {@link BitcashWalletsDocument}, wrapped to the request's `wrapPubkey`. */
    wrappedDocument?: string;

    // ── save-prepare, approved ──
    /** Base64 of the CID-side ephemeral 1600-byte hybrid public key. */
    cidWrapPubkey?: string;

    // ── save, approved ──
    /** The slot the wallet took. Stable per wallet and never reused, so the
     *  module records it on its local row. */
    slot?: number;

    _senderServiceId?: string;
}

/**
 * Injected surface for the dispatcher. Every side-effect — the dialog, the
 * policy store, the blob reads and writes, the ephemeral stash — so the gate
 * logic is unit-testable with no DOM, no network and no keybag.
 */
export interface BitcashWalletsDispatchDeps {
    /** The active identity's cid, or null when none is signed in. */
    getActiveCid: () => string | null;
    /** True when the in-memory keybag is loaded. */
    isUnlocked: () => boolean;
    /** Resolves when the keybag is loaded. Raced against the budget below. */
    waitForUnlock: () => Promise<void>;
    /** Override for the wait-for-unlock budget. Tests only — production uses
     *  the shared `unlock-gate.UNLOCK_WAIT_TIMEOUT_MS` (5 min). */
    unlockWaitTimeoutMs?: number;
    /** Ask the hub to raise the CID tab for this request (M2c). Test seam;
     *  production uses the socket registered by `key-grant/client.ts`. */
    requestFocus?: (requestId: string) => void;

    /** The consent dialog. Absent ⇒ the release refuses
     *  (`consent-ui-unavailable`): spending capability never leaves without
     *  asking. A request that declares no interaction and has no standing
     *  grant is refused `consent-required` BEFORE the dialog would open — the
     *  hub never raised this tab for it, so nobody could answer. */
    confirmWalletsRelease?: (
        input: BitcashWalletsReleaseConsentInput,
    ) => Promise<BitcashWalletsReleaseConsentDecision>;
    checkPolicy?: (cid: string, requesterId: string, keyId: string) => 'always' | 'session' | 'none';
    grantAlways?: (cid: string, entry: Omit<GrantPolicyEntry, 'scope' | 'grantedAt'>) => void;
    grantSession?: (cid: string, entry: Omit<GrantPolicyEntry, 'scope' | 'grantedAt'>) => void;

    /** The member's wallet document and its generation, or null when the
     *  account has none. Throws `cid-locked` / `derive-root-missing`. */
    loadWallets: () => Promise<{ doc: BitcashWalletsDocument; generation: number } | null>;
    /** Append one wallet at the next free slot, compare-and-set with retry. */
    addWallet: (record: NewWalletRecord) => Promise<{ slot: number; generation: number }>;

    /** Leg 1's stash. Defaults to `bitcash-wallets-save-store.ts`. */
    prepareSave?: (requestId: string, requesterId: string) => Uint8Array;
    consumeSave?: (requestId: string) => { secretKey: Uint8Array; requesterId: string } | null;
    /** Keygen seam for the prepare leg; tests pin it. */
    keygen?: () => KemKeyPair;
    /** Clock seam for tests. */
    now?: () => number;
}
